import React, { useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  X, Save, Loader2, Trash2, Plus, RotateCcw, Settings2, ChevronDown, ChevronRight, Users,
} from "lucide-react";
import GenderPresetsModal from "./GenderPresetsModal";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import useUnsavedGuard from "../hooks/useUnsavedGuard";
import UnsavedChangesDialog from "./UnsavedChangesDialog";
import FormError from "./ui/FormError";
import MarkerOutOfRangeModal from "./MarkerOutOfRangeModal";
import { confirmDialog } from "../utils/dialogs";

/**
 * Admin: edit reference ranges live (no deploy).
 *
 * Each parameter row shows the default low/high + a list of sex/age
 * overrides. Overrides are evaluated top-down at status-computation time.
 *
 * Tip card explains the override priority so admins don't accidentally
 * shadow a more-specific rule with a broader one.
 *
 * ESC-with-unsaved-changes protection: since each row already has an
 * explicit Save button (per-row PUT), auto-save is intentionally NOT
 * enabled here — admins want to review the whole matrix before
 * committing. What IS wired up: Escape / overlay-click with pending row
 * edits pops the Unsaved-changes dialog offering "Save & close" (bulk
 * PUT of every dirty row), "Discard", or "Keep editing".
 */
export default function ParamRangesModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dirty, setDirty] = useState({});  // key -> patched copy
  const [filter, setFilter] = useState("");
  // Drill-down modal: double-click a marker row → show every athlete
  // whose latest lab flagged that marker out of range.
  const [drillMarker, setDrillMarker] = useState(null);  // {key, label} | null
  const [presetsOpen, setPresetsOpen] = useState(false);
  const reloadRanges = () => api.get("/admin/param-ranges").then((r) => setData(r.data)).catch(() => {});

  const hasDirty = Object.keys(dirty).length > 0;

  useEffect(() => {
    if (!open) return;
    setError("");
    setDirty({});
    setLoading(true);
    api.get("/admin/param-ranges")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Could not load ranges"))
      .finally(() => setLoading(false));
  }, [open]);

  const view = (key) => dirty[key] || data?.ranges.find((r) => r.key === key);

  const patch = (key, patcher) => {
    const current = view(key);
    if (!current) return;
    const next = patcher({
      default: { ...(current.default || {}) },
      overrides: (current.overrides || []).map((o) => ({ ...o, when: { ...(o.when || {}) } })),
    });
    setDirty((d) => ({ ...d, [key]: { ...current, ...next } }));
  };

  const save = async (key) => {
    const v = view(key);
    if (!v) return;
    setSavingKey(key);
    setError("");
    try {
      await api.put(`/admin/param-ranges/${key}`, {
        default: v.default || {},
        overrides: (v.overrides || []).map((o) => ({
          when: o.when || {}, low: o.low, high: o.high, source: o.source || null,
        })),
      });
      // Refresh just this row.
      const r = await api.get("/admin/param-ranges");
      setData(r.data);
      setDirty((d) => {
        const { [key]: _gone, ...rest } = d;
        return rest;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  // Bulk-save every dirty row — used by the Unsaved-changes dialog's
  // "Save & close" action. Serialised so one PUT failing surfaces the
  // exact key that broke without corrupting the rest.
  const saveAllDirty = useCallback(async () => {
    if (!hasDirty) return;
    setBulkSaving(true);
    setError("");
    try {
      for (const [key, v] of Object.entries(dirty)) {
        await api.put(`/admin/param-ranges/${key}`, {
          default: v.default || {},
          overrides: (v.overrides || []).map((o) => ({
            when: o.when || {}, low: o.low, high: o.high, source: o.source || null,
          })),
        });
      }
      const r = await api.get("/admin/param-ranges");
      setData(r.data);
      setDirty({});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Bulk save failed");
      throw e;
    } finally {
      setBulkSaving(false);
    }
  }, [dirty, hasDirty]);

  const resetAll = async () => {
    if (!(await confirmDialog("Reset ALL parameter ranges to the original Tata 1mg defaults? This clears every override across every parameter.", { confirmLabel: "Reset all", danger: true }))) return;
    setLoading(true);
    setError("");
    try {
      await api.post("/admin/param-ranges/reset-defaults");
      const r = await api.get("/admin/param-ranges");
      setData(r.data);
      setDirty({});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.ranges;
    return data.ranges.filter((r) =>
      r.key.toLowerCase().includes(q) ||
      (r.label || "").toLowerCase().includes(q) ||
      (r.group || "").toLowerCase().includes(q),
    );
  }, [data, filter]);

  // Group the filtered rows into lab panels for the sectioned display.
  // Ordering follows how a clinician actually reads a report: CBC first,
  // then chemistries, endocrine, vitamins, urine. "Other" comes last.
  const _PANEL_ORDER = [
    "Complete Blood Count", "Lipid Profile", "Iron studies", "Diabetes",
    "Kidney profile", "Liver profile", "Thyroid", "Vitamins", "Minerals",
    "Inflammation", "Endocrine / Bone", "Urine", "Biometrics", "Other",
  ];
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const r of filtered) {
      const p = r.group || "Other";
      if (!buckets.has(p)) buckets.set(p, []);
      buckets.get(p).push(r);
    }
    return _PANEL_ORDER
      .filter((p) => buckets.has(p))
      .concat([...buckets.keys()].filter((p) => !_PANEL_ORDER.includes(p)).sort())
      .map((p) => ({ panel: p, rows: buckets.get(p) }));
  }, [filtered]);

  const [collapsedPanels, setCollapsedPanels] = useState(new Set());
  const togglePanel = (p) => setCollapsedPanels((prev) => {
    const s = new Set(prev);
    if (s.has(p)) s.delete(p); else s.add(p);
    return s;
  });

  // Colour palette for the panel-header pills. Chosen so each panel has
  // a distinct clinical feel while staying on-brand with the dashboard's
  // cool-navy backdrop.
  const _PANEL_TONE = {
    "Complete Blood Count": { bg: "rgba(239,68,68,0.14)",   br: "rgba(239,68,68,0.55)",   fg: "#FCA5A5" },
    "Lipid Profile":        { bg: "rgba(245,158,11,0.14)",  br: "rgba(245,158,11,0.55)",  fg: "#FCD34D" },
    "Iron studies":         { bg: "rgba(217,70,239,0.14)",  br: "rgba(217,70,239,0.55)",  fg: "#F0ABFC" },
    "Diabetes":             { bg: "rgba(251,113,133,0.14)", br: "rgba(251,113,133,0.55)", fg: "#FDA4AF" },
    "Kidney profile":       { bg: "rgba(56,189,248,0.14)",  br: "rgba(56,189,248,0.55)",  fg: "#7DD3FC" },
    "Liver profile":        { bg: "rgba(163,230,53,0.14)",  br: "rgba(163,230,53,0.55)",  fg: "#BEF264" },
    "Thyroid":              { bg: "rgba(168,85,247,0.14)",  br: "rgba(168,85,247,0.55)",  fg: "#D8B4FE" },
    "Vitamins":             { bg: "rgba(139,92,246,0.14)",  br: "rgba(139,92,246,0.55)",  fg: "#C4B5FD" },
    "Minerals":             { bg: "rgba(20,184,166,0.14)",  br: "rgba(20,184,166,0.55)",  fg: "#5EEAD4" },
    "Inflammation":         { bg: "rgba(244,63,94,0.14)",   br: "rgba(244,63,94,0.55)",   fg: "#FDA4AF" },
    "Endocrine / Bone":     { bg: "rgba(6,182,212,0.14)",   br: "rgba(6,182,212,0.55)",   fg: "#67E8F9" },
    "Urine":                { bg: "rgba(250,204,21,0.14)",  br: "rgba(250,204,21,0.55)",  fg: "#FDE68A" },
    "Biometrics":           { bg: "rgba(16,185,129,0.14)",  br: "rgba(16,185,129,0.55)",  fg: "#6EE7B7" },
    "Other":                { bg: "rgba(148,163,184,0.12)", br: "rgba(148,163,184,0.35)", fg: "#CBD5E1" },
  };
  const _panelTone = (p) => _PANEL_TONE[p] || _PANEL_TONE["Other"];

  // Wire the unsaved-changes guard. `isDirty` short-circuits scheduleSave —
  // we're just leveraging the ESC / overlay-click confirmation flow.
  const guardSave = useCallback(async () => {
    await saveAllDirty();
  }, [saveAllDirty]);

  const guard = useUnsavedGuard({
    open,
    getPayload: () => (hasDirty ? { rows: Object.keys(dirty) } : null),
    onSave: guardSave,
    onClose,
    enabled: true,
    isDirty: hasDirty,
  });

  useEscClose(open && !guard.dirty, () => { if (!savingKey && !bulkSaving) onClose?.(); });

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-3"
      data-testid="param-ranges-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !savingKey && !bulkSaving) guard.handleClose(); }}
    >
      <div className="card w-full max-w-5xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <Settings2 size={12} /> Admin
              {hasDirty && (
                <span className="inline-flex items-center gap-1 text-amber-300 text-[10px] normal-case tracking-normal" data-testid="ranges-dirty-badge">
                  · {Object.keys(dirty).length} unsaved row{Object.keys(dirty).length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="font-display text-lg text-slate-100">Reference ranges</div>
            <div className="text-[11px] text-slate-400 mt-0.5 max-w-2xl leading-snug">
              Edit the low/high bounds for each diagnostic marker. Add sex / age overrides for athlete-specific cut-offs (e.g. Hb &gt; 13.5 for adult males). Changes take effect immediately — no deploy needed.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setPresetsOpen(true)} disabled={loading || savingKey || bulkSaving || hasDirty}
                    title={hasDirty ? "Save or discard row edits first" : "Male / female ranges"}
                    className="pill disabled:opacity-40" data-testid="gender-presets-btn">
              <Users size={12} /> Gender presets
            </button>
            <button onClick={resetAll} disabled={loading || savingKey || bulkSaving}
                    className="pill disabled:opacity-40" data-testid="reset-ranges-btn">
              <RotateCcw size={12} /> Reset all
            </button>
            <button onClick={guard.handleClose} className="pill !p-1.5" data-testid="ranges-close-btn">
              <X size={14} />
            </button>
          </div>
        </div>

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter — hb, vit d, iron, electrolytes…"
          className="w-full mb-3 px-3 py-2 bg-[#0E1A2E] border border-[#1E304B] rounded text-[12px]"
          data-testid="ranges-filter"
        />

        {error && <div className="mb-3"><FormError message={error} onDismiss={() => setError("")} testId="ranges-error" /></div>}

        {loading ? (
          <div className="py-10 text-center text-slate-400">
            <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
          </div>
        ) : (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1" data-testid="ranges-groups">
            {grouped.length === 0 && (
              <div className="py-8 text-center text-slate-500 text-[12px]">No markers match this filter.</div>
            )}
            {grouped.map(({ panel, rows }) => {
              const collapsed = collapsedPanels.has(panel);
              const dirtyCount = rows.filter((r) => dirty[r.key]).length;
              const tone = _panelTone(panel);
              return (
                <div
                  key={panel}
                  className="rounded-lg overflow-hidden"
                  style={{ border: `1px solid ${tone.br}`, background: `${tone.bg}` }}
                  data-testid={`ranges-panel-${panel}`}
                >
                  <button
                    onClick={() => togglePanel(panel)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors"
                    style={{
                      background: `linear-gradient(90deg, ${tone.bg} 0%, transparent 60%)`,
                      borderBottom: collapsed ? "none" : `1px solid ${tone.br}`,
                    }}
                    data-testid={`ranges-panel-toggle-${panel}`}
                  >
                    <span className="text-[11px] font-mono-data" style={{ color: tone.fg }}>
                      {collapsed ? "▸" : "▾"}
                    </span>
                    <span
                      className="text-[13px] font-display uppercase tracking-wider"
                      style={{ color: tone.fg }}
                    >
                      {panel}
                    </span>
                    <span
                      className="text-[10px] font-mono-data px-1.5 py-0.5 rounded-full ml-1"
                      style={{
                        background: `${tone.bg}`,
                        border: `1px solid ${tone.br}`,
                        color: tone.fg,
                      }}
                    >
                      {rows.length} marker{rows.length === 1 ? "" : "s"}
                    </span>
                    {dirtyCount > 0 && (
                      <span className="text-[10px] text-amber-300 font-mono-data ml-1">
                        · {dirtyCount} unsaved
                      </span>
                    )}
                  </button>
                  {!collapsed && (
                    <div className="space-y-1.5 p-2 bg-[#0B1627]">
                      {rows.map((r) => (
                        <ParamRow
                          key={r.key}
                          row={view(r.key)}
                          isDirty={!!dirty[r.key]}
                          expanded={!!expanded[r.key]}
                          onToggle={() => setExpanded((p) => ({ ...p, [r.key]: !p[r.key] }))}
                          onPatch={(p) => patch(r.key, p)}
                          onSave={() => save(r.key)}
                          saving={savingKey === r.key}
                          onDrillDown={() => setDrillMarker({ key: r.key, label: r.label })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <UnsavedChangesDialog
        open={guard.confirming === "esc"}
        onSave={guard.confirmSave}
        onDiscard={guard.confirmDiscard}
        onKeep={guard.confirmKeep}
        saving={bulkSaving}
      />
      <MarkerOutOfRangeModal
        open={!!drillMarker}
        markerKey={drillMarker?.key}
        markerLabel={drillMarker?.label}
        onClose={() => setDrillMarker(null)}
      />
      <GenderPresetsModal
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        onApplied={reloadRanges}
      />
    </div>,
    document.body,
  );
}

function ParamRow({ row, isDirty, expanded, onToggle, onPatch, onSave, saving, onDrillDown }) {
  if (!row) return null;
  const d = row.default || {};
  const isQualitative = (d.source || "").toLowerCase().startsWith("qualitative");
  return (
    <div
      className={`rounded border ${isDirty ? "border-amber-500/50 bg-amber-500/[0.05]" : "border-[#1E304B] bg-[#0B1627]"}`}
      data-testid={`range-row-${row.key}`}
      onDoubleClick={(e) => { e.stopPropagation(); onDrillDown?.(); }}
      title="Double-click to see every athlete outside this range"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={onToggle} className="p-0.5 text-slate-500 hover:text-slate-200" data-testid={`range-expand-${row.key}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-slate-100 font-display truncate">
            {row.label} <span className="text-slate-500 text-[10px] font-mono-data">· {row.key}</span>
          </div>
          <div className="text-[10px] text-slate-500 font-mono-data">
            {isQualitative ? (
              <span className="text-amber-200/80">
                {d.source /* the "Qualitative — expected …" hint we seeded on backend */}
              </span>
            ) : (
              <>
                default {d.low ?? "—"} – {d.high ?? "—"} {row.unit}
              </>
            )}
            {row.overrides?.length > 0 && <span className="text-cyan-300 ml-2">+ {row.overrides.length} override{row.overrides.length === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <NumberInput label="Low" value={d.low}  onChange={(v) => onPatch((p) => ({ ...p, default: { ...p.default, low: v }  }))} testid={`range-default-low-${row.key}`} />
        <NumberInput label="High" value={d.high} onChange={(v) => onPatch((p) => ({ ...p, default: { ...p.default, high: v } }))} testid={`range-default-high-${row.key}`} />
        {isDirty ? (
          <button onClick={onSave} disabled={saving} className="pill active text-[11px]" data-testid={`range-save-${row.key}`}>
            {saving ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />}
            Save
          </button>
        ) : (
          <span className="text-[10px] text-slate-600 font-mono-data px-2">saved</span>
        )}
      </div>

      {expanded && (
        <div className="px-3 pb-3 border-t border-[#1E304B] bg-[#0E1A2E]/40">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2 mb-1.5">Overrides (top-down, first match wins)</div>
          {(row.overrides || []).map((o, idx) => (
            <OverrideRow
              key={`${row.key}-ovr-${idx}-${o.source || ""}`}
              ovr={o}
              onChange={(next) => onPatch((p) => ({
                ...p,
                overrides: p.overrides.map((x, i) => i === idx ? { ...x, ...next, when: { ...x.when, ...(next.when || {}) } } : x),
              }))}
              onRemove={() => onPatch((p) => ({ ...p, overrides: p.overrides.filter((_, i) => i !== idx) }))}
              testid={`override-${row.key}-${idx}`}
            />
          ))}
          <button
            onClick={() => onPatch((p) => ({
              ...p,
              overrides: [...p.overrides, { when: {}, low: null, high: null, source: "" }],
            }))}
            className="pill text-[10px] mt-1.5"
            data-testid={`add-override-${row.key}`}
          >
            <Plus size={10} /> Add override
          </button>
          <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
            Order matters — put the most specific rule (e.g. sex + age band) first. An override applies only when ALL its when-fields match; an empty when-field is &ldquo;any&rdquo;.
          </div>
        </div>
      )}
    </div>
  );
}

function OverrideRow({ ovr, onChange, onRemove, testid }) {
  const w = ovr.when || {};
  return (
    <div className="flex flex-wrap items-center gap-1.5 my-1 px-2 py-1.5 rounded border border-[#1E304B] bg-[#0B1627]" data-testid={testid}>
      <span className="text-[9px] uppercase tracking-widest text-slate-500">When</span>
      <select
        value={w.sex || ""}
        onChange={(e) => onChange({ when: { sex: e.target.value || null } })}
        className="form-input py-0.5 text-[11px]"
        data-testid={`${testid}-sex`}
      >
        <option value="">any sex</option>
        <option value="M">male</option>
        <option value="F">female</option>
      </select>
      <NumberInput label="age ≥" value={w.age_min} onChange={(v) => onChange({ when: { age_min: v } })} testid={`${testid}-age-min`} compact />
      <NumberInput label="age ≤" value={w.age_max} onChange={(v) => onChange({ when: { age_max: v } })} testid={`${testid}-age-max`} compact />
      <span className="text-[9px] uppercase tracking-widest text-slate-500 ml-1">⇒</span>
      <NumberInput label="low"  value={ovr.low}  onChange={(v) => onChange({ low: v  })} testid={`${testid}-low`} />
      <NumberInput label="high" value={ovr.high} onChange={(v) => onChange({ high: v })} testid={`${testid}-high`} />
      <input
        value={ovr.source || ""}
        onChange={(e) => onChange({ source: e.target.value })}
        placeholder="source / note"
        className="form-input py-0.5 text-[11px] flex-1 min-w-[140px]"
        data-testid={`${testid}-source`}
      />
      <button onClick={onRemove} className="p-1 text-rose-300 hover:text-rose-100" data-testid={`${testid}-remove`}>
        <Trash2 size={11} />
      </button>
    </div>
  );
}

function NumberInput({ label, value, onChange, testid, compact = false }) {
  return (
    <label className="inline-flex items-center gap-1">
      <span className="text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
      <input
        type="number"
        step="0.01"
        value={value == null ? "" : value}
        onChange={(e) => {
          const t = e.target.value.trim();
          onChange(t === "" ? null : Number(t));
        }}
        className={`form-input py-0.5 text-[11px] text-right font-mono-data ${compact ? "w-14" : "w-16"}`}
        data-testid={testid}
      />
    </label>
  );
}
